import React, { useCallback } from 'react';

import { type EntryPoint, type EntryPointComponent } from 'react-relay';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { PopupTrigger, type Props } from '@atlassian/entry-point-popup';

import { DialogContext } from '../../../analytics/dialog-context';
import { useEntrypointErrorAnalytics } from '../../../analytics/use-entrypoint-error-analytics';
import { useOnCloseDialog } from '../../../analytics/use-on-close-dialog';
import { EditionAwarenessButton } from '../../edition-awareness-button';

import type { DialogConfig } from './types';

const styles = cssMap({
	fallbackBox: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

type AnyEntryPoint = EntryPoint<EntryPointComponent<any, any, any, any>, any>;

type PopupEntryPointInnerProps<T> = Omit<Props<T>, 'trigger'>;

export type PopupEntryPointProps<T> = PopupEntryPointInnerProps<T> & {
	dialogConfig: DialogConfig;
};

export function PopupEntryPoint<TEntryPoint extends AnyEntryPoint>(
	props: PopupEntryPointProps<TEntryPoint>,
): React.JSX.Element {
	const { dialogConfig, ...rest } = props;

	return (
		<DialogContext.Provider value={dialogConfig}>
			<AnalyticsContext data={dialogConfig}>
				<PopupEntryPointInner<TEntryPoint> {...rest} />
			</AnalyticsContext>
		</DialogContext.Provider>
	);
}

export function PopupEntryPointInner<TEntryPoint extends AnyEntryPoint>(
	props: PopupEntryPointInnerProps<TEntryPoint>,
): React.JSX.Element {
	const { onClose, ...rest } = props;

	const onError = useEntrypointErrorAnalytics();
	const onCloseDialog = useOnCloseDialog();

	const onClosePopup = useCallback(
		(
			event: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null,
			currentLevel?: any,
		) => {
			onClose?.(event, currentLevel);
			onCloseDialog();
		},
		[onClose, onCloseDialog],
	);

	return (
		<PopupTrigger
			{...rest}
			shouldFitContainer={false}
			trigger={({ ref }) => (
				<EditionAwarenessButton ref={ref as unknown as React.Ref<HTMLButtonElement>} />
			)}
			fallback={<PopupFallback />}
			onError={onError}
			onClose={onClosePopup}
			preloadOnFocus={true}
		/>
	);
}

const PopupFallback = () => (
	<Box xcss={styles.fallbackBox}>
		<Spinner />
	</Box>
);
